import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import DashboardHeader from '../components/DashboardHeader';
import PassagePicker from '../components/PassagePicker';
import { useDb } from '../utils/DbContext';
import './Dashboard.css';

function McQuestionFieldGroup({
  question,
  index,
  onFieldChange,
  showDelete,
  onDelete,
  perRowLevel,
}) {
  return (
    <div className="generated-question-item">
      <div className="question-item-header">
        <h4 className="question-item-title">Question {index + 1}</h4>
        {showDelete && (
          <button
            type="button"
            className="delete-question-button"
            onClick={() => onDelete(index)}
            title="Delete this question"
          >
            Delete
          </button>
        )}
      </div>
      {perRowLevel && (
        <div className="form-group">
          <label>
            Level <span className="required">*</span>
          </label>
          <input
            type="number"
            value={question.level ?? ''}
            onChange={(e) =>
              onFieldChange(index, 'level', parseInt(e.target.value, 10) || 0)
            }
            required
            min="0"
            placeholder="Difficulty level (integer)"
          />
        </div>
      )}
      <div className="form-group">
        <label>
          Question <span className="required">*</span>
        </label>
        <textarea
          value={question.question}
          onChange={(e) => onFieldChange(index, 'question', e.target.value)}
          required
          rows={3}
          placeholder="Question stem..."
        />
      </div>
      <div className="form-group">
        <label>
          Correct option <span className="required">*</span>
        </label>
        <textarea
          value={question.correct}
          onChange={(e) => onFieldChange(index, 'correct', e.target.value)}
          required
          rows={2}
          placeholder="Correct answer..."
        />
      </div>
      <div className="form-group">
        <label>
          Explanation (correct) <span className="required">*</span>
        </label>
        <textarea
          value={question.correctExplain}
          onChange={(e) => onFieldChange(index, 'correctExplain', e.target.value)}
          required
          rows={3}
          placeholder="Why the correct option is correct..."
        />
      </div>
      <div className="form-group">
        <label>
          Wrong option 1 <span className="required">*</span>
        </label>
        <textarea
          value={question.wrong1}
          onChange={(e) => onFieldChange(index, 'wrong1', e.target.value)}
          required
          rows={2}
        />
      </div>
      <div className="form-group">
        <label>
          Wrong option 1 explain <span className="required">*</span>
        </label>
        <textarea
          value={question.wrongExplain1 ?? ''}
          onChange={(e) => onFieldChange(index, 'wrongExplain1', e.target.value)}
          required
          rows={2}
          placeholder="Why wrong option 1 is incorrect..."
        />
      </div>
      <div className="form-group">
        <label>
          Wrong option 2 <span className="required">*</span>
        </label>
        <textarea
          value={question.wrong2}
          onChange={(e) => onFieldChange(index, 'wrong2', e.target.value)}
          required
          rows={2}
        />
      </div>
      <div className="form-group">
        <label>
          Wrong option 2 explain <span className="required">*</span>
        </label>
        <textarea
          value={question.wrongExplain2 ?? ''}
          onChange={(e) => onFieldChange(index, 'wrongExplain2', e.target.value)}
          required
          rows={2}
          placeholder="Why wrong option 2 is incorrect..."
        />
      </div>
      <div className="form-group">
        <label>
          Wrong option 3 <span className="required">*</span>
        </label>
        <textarea
          value={question.wrong3}
          onChange={(e) => onFieldChange(index, 'wrong3', e.target.value)}
          required
          rows={2}
        />
      </div>
      <div className="form-group">
        <label>
          Wrong option 3 explain <span className="required">*</span>
        </label>
        <textarea
          value={question.wrongExplain3 ?? ''}
          onChange={(e) => onFieldChange(index, 'wrongExplain3', e.target.value)}
          required
          rows={2}
          placeholder="Why wrong option 3 is incorrect..."
        />
      </div>
    </div>
  );
}

const MC_EDIT_SEARCH_COLUMNS = [
  { key: 'question', label: 'Question' },
  { key: 'correct', label: 'Correct' },
  { key: 'correctExplain', label: 'Explanation (correct)' },
  { key: 'wrong1', label: 'Wrong 1' },
  { key: 'wrong2', label: 'Wrong 2' },
  { key: 'wrong3', label: 'Wrong 3' },
  { key: 'wrongExplain1', label: 'Wrong explain 1' },
  { key: 'wrongExplain2', label: 'Wrong explain 2' },
  { key: 'wrongExplain3', label: 'Wrong explain 3' },
  { key: 'level', label: 'Level' },
  { key: 'multipleChoiceId', label: 'ID' },
];

const defaultMcEditSearchColumns = Object.fromEntries(
  MC_EDIT_SEARCH_COLUMNS.map(({ key }) => [key, false])
);

const LQ_SEARCH_COLUMNS = [
  { key: 'questionValue', label: 'Question (long_question.value)' },
  { key: 'answerValue', label: 'Answer (long_answer.value)' },
];

const defaultLqSearchColumns = Object.fromEntries(
  LQ_SEARCH_COLUMNS.map(({ key }) => [key, false])
);

const Dashboard = () => {
  const { dbName } = useDb();
  const navigate = useNavigate();
  const location = useLocation();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [columns, setColumns] = useState([]);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Long question form state
  const [longQuestionValue, setLongQuestionValue] = useState('');
  const [selectedPassageId, setSelectedPassageId] = useState(null);
  const [longAnswerLines, setLongAnswerLines] = useState('');
  const [passages, setPassages] = useState([]);
  const [longQALoading, setLongQALoading] = useState(false);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  
  // AI generation state
  const [aiPrompt, setAiPrompt] = useState('模仿香港DSE中文科考卷，擬定《》的長答題及答案。');
  const [questionCount, setQuestionCount] = useState(40);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [modalPassageId, setModalPassageId] = useState(null);
  const [insertLoading, setInsertLoading] = useState(false);
  
  // Keyword form state
  const [keywordPrompt, setKeywordPrompt] = useState('請擬定會出現於香港DSE中文考範文考試，與篇章《出師表》相關的關鍵字（非原文句子）');
  const [keywordCount, setKeywordCount] = useState(20);
  const [generatedKeywords, setGeneratedKeywords] = useState([]);
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [keywordGenerationLoading, setKeywordGenerationLoading] = useState(false);
  const [keywordModalPassageId, setKeywordModalPassageId] = useState(null);
  const [keywordInsertLoading, setKeywordInsertLoading] = useState(false);
  const [keywordLines, setKeywordLines] = useState('');
  const [selectedKeywordPassageId, setSelectedKeywordPassageId] = useState(null);
  const [keywordLoading, setKeywordLoading] = useState(false);
  
  // Multiple choice — AI generation + review modal
  const [mcAiPrompt, setMcAiPrompt] = useState('');
  const [mcQuestionCount, setMcQuestionCount] = useState(10);
  const [generatedMcQuestions, setGeneratedMcQuestions] = useState([]);
  const [showMcModal, setShowMcModal] = useState(false);
  const [mcGenerationLoading, setMcGenerationLoading] = useState(false);
  const [mcInsertLoading, setMcInsertLoading] = useState(false);
  const [mcModalPassageId, setMcModalPassageId] = useState(null);
  const [mcLevel, setMcLevel] = useState(1);

  const [mcEditSelectedPassageId, setMcEditSelectedPassageId] = useState(null);
  const [mcEditLoadedPassageId, setMcEditLoadedPassageId] = useState(null);
  const [editMcRows, setEditMcRows] = useState([]);
  const [mcEditFetchLoading, setMcEditFetchLoading] = useState(false);
  const [mcEditSaveLoading, setMcEditSaveLoading] = useState(false);
  const [mcEditSearchQuery, setMcEditSearchQuery] = useState('');
  const [mcEditSearchColumns, setMcEditSearchColumns] = useState(
    () => ({ ...defaultMcEditSearchColumns })
  );
  const [mcPassages, setMcPassages] = useState([]);
  const [mcPassagesLoading, setMcPassagesLoading] = useState(false);
  const [mcPassagesError, setMcPassagesError] = useState('');

  // Long Question edit state
  const [lqSelectedPassageId, setLqSelectedPassageId] = useState(null);
  const [lqLoadedPassageId, setLqLoadedPassageId] = useState(null);
  const [lqRows, setLqRows] = useState([]);
  const [lqFetchLoading, setLqFetchLoading] = useState(false);
  const [lqSaveLoading, setLqSaveLoading] = useState(false);
  const [lqSearchQuery, setLqSearchQuery] = useState('');
  const [lqSearchColumns, setLqSearchColumns] = useState(() => ({ ...defaultLqSearchColumns }));

  // View state for sidebar navigation
  const [activeView, setActiveView] = useState('tables'); // 'tables', 'long-question', 'keywords', 'multiple-choice'

  useEffect(() => {
    fetchTables();
    fetchPassages();
  }, []);

  useEffect(() => {
    const s = location.state;
    if (!s || typeof s !== 'object') return;
    const views = new Set(['tables', 'long-question', 'keywords', 'multiple-choice']);
    let applied = false;
    if (views.has(s.activeView)) {
      setActiveView(s.activeView);
      applied = true;
    }
    if (typeof s.selectedTable === 'string') {
      setSelectedTable(s.selectedTable);
      applied = true;
    }
    if (applied) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (activeView === 'multiple-choice') {
      fetchMcPassages();
    }
  }, [activeView]);

  useEffect(() => {
    if (selectedTable) {
      fetchColumns(selectedTable);
    } else {
      setColumns([]);
      setFormData({});
    }
  }, [selectedTable]);

  useEffect(() => {
    if (
      mcEditLoadedPassageId !== null &&
      mcEditSelectedPassageId !== mcEditLoadedPassageId
    ) {
      setMcEditLoadedPassageId(null);
      setEditMcRows([]);
      setMcEditSearchQuery('');
      setMcEditSearchColumns({ ...defaultMcEditSearchColumns });
    }
  }, [mcEditSelectedPassageId, mcEditLoadedPassageId]);

  const mcEditFilteredItems = useMemo(() => {
    const q = mcEditSearchQuery.trim().toLowerCase();
    const activeKeys = MC_EDIT_SEARCH_COLUMNS.filter(
      ({ key }) => mcEditSearchColumns[key]
    ).map(({ key }) => key);
    if (!q) {
      return editMcRows.map((row, originalIndex) => ({ row, originalIndex }));
    }
    if (activeKeys.length === 0) {
      return [];
    }
    return editMcRows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) =>
        activeKeys.some((col) =>
          String(row[col] ?? '')
            .toLowerCase()
            .includes(q)
        )
      );
  }, [editMcRows, mcEditSearchQuery, mcEditSearchColumns]);

  const mcEditSearchActiveColumnCount = useMemo(
    () =>
      MC_EDIT_SEARCH_COLUMNS.filter(({ key }) => mcEditSearchColumns[key]).length,
    [mcEditSearchColumns]
  );

  const mcEditSearchFilteringActive =
    !!mcEditSearchQuery.trim() && mcEditSearchActiveColumnCount > 0;

  const mcEditSearchNeedsColumns =
    !!mcEditSearchQuery.trim() && mcEditSearchActiveColumnCount === 0;

  const fetchTables = async () => {
    try {
      const response = await api.get('/data/tables');
      setTables(response.data.tables);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch tables' });
    }
  };

  const fetchPassages = async () => {
    setPassagesLoading(true);
    setPassagesError('');
    try {
      const response = await api.get('/data/passages');
      setPassages(response.data.passages || []);
      if (!response.data.passages || response.data.passages.length === 0) {
        setPassagesError('No passages found in the database');
      }
    } catch (error) {
      console.error('Failed to fetch passages:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Failed to load passages. Please try again.';
      setPassagesError(errorMessage);
      setPassages([]);
    } finally {
      setPassagesLoading(false);
    }
  };

  const fetchMcPassages = async () => {
    setMcPassagesLoading(true);
    setMcPassagesError('');
    try {
      const response = await api.get('/data/passages', { params: { all: '1' } });
      setMcPassages(response.data.passages || []);
      if (!response.data.passages || response.data.passages.length === 0) {
        setMcPassagesError('No passages found in the database');
      }
    } catch (error) {
      console.error('Failed to fetch MC passages:', error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.details ||
        'Failed to load passages. Please try again.';
      setMcPassagesError(errorMessage);
      setMcPassages([]);
    } finally {
      setMcPassagesLoading(false);
    }
  };

  const fetchColumns = async (tableName) => {
    try {
      const response = await api.get(`/data/tables/${tableName}/columns`);
      setColumns(response.data.columns);
      const initialData = {};
      response.data.columns.forEach((col) => {
        if (!col.column_default && col.is_nullable === 'YES') {
          initialData[col.column_name] = '';
        }
      });
      setFormData(initialData);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch columns' });
    }
  };

  const handleInputChange = (columnName, value) => {
    setFormData({
      ...formData,
      [columnName]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTable) {
      setMessage({ type: 'error', text: 'Please select a table' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const dataToSend = {};
      columns.forEach((col) => {
        const value = formData[col.column_name];
        if (value !== undefined && value !== '') {
          if (col.data_type === 'integer' || col.data_type === 'bigint') {
            dataToSend[col.column_name] = parseInt(value);
          } else if (col.data_type === 'numeric' || col.data_type === 'double precision') {
            dataToSend[col.column_name] = parseFloat(value);
          } else if (col.data_type === 'boolean') {
            dataToSend[col.column_name] = value === 'true' || value === '1';
          } else {
            dataToSend[col.column_name] = value;
          }
        }
      });

      await api.post(`/data/insert/${selectedTable}`, dataToSend);
      setMessage({ type: 'success', text: 'Data inserted successfully!' });
      setFormData({});
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to insert data',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLongQASubmit = async (e) => {
    e.preventDefault();
    
    if (!longQuestionValue.trim()) {
      setMessage({ type: 'error', text: 'Please enter a question' });
      return;
    }
    
    if (!selectedPassageId) {
      setMessage({ type: 'error', text: 'Please select a passage' });
      return;
    }
    
    const answerLines = longAnswerLines.split('\n').filter(line => line.trim() !== '');
    if (answerLines.length === 0) {
      setMessage({ type: 'error', text: 'Please enter at least one answer' });
      return;
    }

    setLongQALoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/insert-long-question-answer', {
        questionValue: longQuestionValue.trim(),
        passageId: selectedPassageId,
        answers: answerLines,
      });
      
      setMessage({ type: 'success', text: `Successfully inserted question and ${response.data.answers_count} answer(s)!` });
      setLongQuestionValue('');
      setSelectedPassageId(null);
      setLongAnswerLines('');
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to insert question and answers',
      });
    } finally {
      setLongQALoading(false);
    }
  };

  const handleGenerateQuestions = async (e) => {
    e.preventDefault();
    
    if (!aiPrompt.trim()) {
      setMessage({ type: 'error', text: 'Please enter an AI prompt' });
      return;
    }
    
    if (questionCount < 1 || questionCount > 40) {
      setMessage({ type: 'error', text: 'Question count must be between 1 and 40' });
      return;
    }

    setGenerationLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/generate-long-questions', {
        prompt: aiPrompt.trim(),
        questionCount: parseInt(questionCount),
      });
      
      setGeneratedQuestions(response.data.questions || []);
      setShowGenerationModal(true);
      setModalPassageId(null);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.details || 'Failed to generate questions',
      });
    } finally {
      setGenerationLoading(false);
    }
  };

  const handleUpdateGeneratedQuestion = (index, field, value) => {
    const updated = [...generatedQuestions];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setGeneratedQuestions(updated);
  };

  const handleDeleteGeneratedQuestion = (index) => {
    const updated = generatedQuestions.filter((_, i) => i !== index);
    setGeneratedQuestions(updated);
  };

  const handleInsertGeneratedQuestions = async (e) => {
    e.preventDefault();
    
    if (!modalPassageId) {
      setMessage({ type: 'error', text: 'Please select a passage' });
      return;
    }
    
    if (generatedQuestions.length === 0) {
      setMessage({ type: 'error', text: 'No questions to insert' });
      return;
    }

    // Validate all questions have content
    for (let i = 0; i < generatedQuestions.length; i++) {
      if (!generatedQuestions[i].questionText.trim() || !generatedQuestions[i].answerText.trim()) {
        setMessage({ type: 'error', text: `Question ${i + 1} is missing question text or answer text` });
        return;
      }
    }

    setInsertLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/insert-multiple-long-questions', {
        passageId: modalPassageId,
        questions: generatedQuestions,
      });
      
      setMessage({ 
        type: 'success', 
        text: `Successfully inserted ${response.data.inserted_count} question(s) and answer(s)!` 
      });
      setShowGenerationModal(false);
      setGeneratedQuestions([]);
      setModalPassageId(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.details || 'Failed to insert questions',
      });
    } finally {
      setInsertLoading(false);
    }
  };

  const handleGenerateKeywords = async (e) => {
    e.preventDefault();
    
    if (!keywordPrompt.trim()) {
      setMessage({ type: 'error', text: 'Please enter an AI prompt' });
      return;
    }
    
    if (keywordCount < 1 || keywordCount > 100) {
      setMessage({ type: 'error', text: 'Keyword count must be between 1 and 100' });
      return;
    }

    setKeywordGenerationLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/generate-keywords', {
        prompt: keywordPrompt.trim(),
        keywordCount: parseInt(keywordCount),
      });
      
      setGeneratedKeywords(response.data.keywords || []);
      setShowKeywordModal(true);
      setKeywordModalPassageId(null);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.details || 'Failed to generate keywords',
      });
    } finally {
      setKeywordGenerationLoading(false);
    }
  };

  const handleUpdateGeneratedKeyword = (index, value) => {
    const updated = [...generatedKeywords];
    updated[index] = value;
    setGeneratedKeywords(updated);
  };

  const handleDeleteGeneratedKeyword = (index) => {
    const updated = generatedKeywords.filter((_, i) => i !== index);
    setGeneratedKeywords(updated);
  };

  const handleInsertGeneratedKeywords = async (e) => {
    e.preventDefault();
    
    if (!keywordModalPassageId) {
      setMessage({ type: 'error', text: 'Please select a passage' });
      return;
    }
    
    if (generatedKeywords.length === 0) {
      setMessage({ type: 'error', text: 'No keywords to insert' });
      return;
    }

    const validKeywords = generatedKeywords.filter(k => k && k.trim().length > 0);
    if (validKeywords.length === 0) {
      setMessage({ type: 'error', text: 'No valid keywords to insert' });
      return;
    }

    setKeywordInsertLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/insert-keywords', {
        passageId: keywordModalPassageId,
        keywords: validKeywords,
      });
      
      let successMessage = response.data.message || `Successfully inserted ${response.data.keywords_count} keyword(s)!`;
      if (response.data.duplicate_keywords && response.data.duplicate_keywords.length > 0) {
        successMessage += ` Duplicate keywords skipped: ${response.data.duplicate_keywords.join(', ')}`;
      }
      setMessage({ 
        type: 'success', 
        text: successMessage
      });
      setShowKeywordModal(false);
      setGeneratedKeywords([]);
      setKeywordModalPassageId(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.response?.data?.details || 'Failed to insert keywords',
      });
    } finally {
      setKeywordInsertLoading(false);
    }
  };

  const handleKeywordSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedKeywordPassageId) {
      setMessage({ type: 'error', text: 'Please select a passage' });
      return;
    }
    
    const keywordLinesArray = keywordLines.split('\n').filter(line => line.trim() !== '');
    if (keywordLinesArray.length === 0) {
      setMessage({ type: 'error', text: 'Please enter at least one keyword' });
      return;
    }

    setKeywordLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/insert-keywords', {
        passageId: selectedKeywordPassageId,
        keywords: keywordLinesArray,
      });
      
      let successMessage = response.data.message || `Successfully inserted ${response.data.keywords_count} keyword(s)!`;
      if (response.data.duplicate_keywords && response.data.duplicate_keywords.length > 0) {
        successMessage += ` Duplicate keywords skipped: ${response.data.duplicate_keywords.join(', ')}`;
      }
      setMessage({ type: 'success', text: successMessage });
      setSelectedKeywordPassageId(null);
      setKeywordLines('');
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to insert keywords',
      });
    } finally {
      setKeywordLoading(false);
    }
  };

  const handleGenerateMcQuestions = async (e) => {
    e.preventDefault();

    if (!mcAiPrompt.trim()) {
      setMessage({ type: 'error', text: 'Please enter an AI prompt' });
      return;
    }

    if (mcQuestionCount < 1 || mcQuestionCount > 40) {
      setMessage({ type: 'error', text: 'Question count must be between 1 and 40' });
      return;
    }

    setMcGenerationLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/generate-mc-questions', {
        prompt: mcAiPrompt.trim(),
        questionCount: parseInt(mcQuestionCount, 10),
      });

      setGeneratedMcQuestions(response.data.questions || []);
      setShowMcModal(true);
      setMcModalPassageId(null);
    } catch (error) {
      setMessage({
        type: 'error',
        text:
          error.response?.data?.error ||
          error.response?.data?.details ||
          'Failed to generate multiple-choice questions',
      });
    } finally {
      setMcGenerationLoading(false);
    }
  };

  const handleUpdateMcQuestion = (index, field, value) => {
    const updated = [...generatedMcQuestions];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setGeneratedMcQuestions(updated);
  };

  const handleDeleteMcQuestion = (index) => {
    setGeneratedMcQuestions(generatedMcQuestions.filter((_, i) => i !== index));
  };

  const handleInsertMcQuestions = async (e) => {
    e.preventDefault();

    if (!mcModalPassageId) {
      setMessage({ type: 'error', text: 'Please select a passage' });
      return;
    }

    if (generatedMcQuestions.length === 0) {
      setMessage({ type: 'error', text: 'No questions to insert' });
      return;
    }

    const levelInt = parseInt(mcLevel, 10);
    if (Number.isNaN(levelInt)) {
      setMessage({ type: 'error', text: 'Level must be a valid integer' });
      return;
    }

    for (let i = 0; i < generatedMcQuestions.length; i++) {
      const q = generatedMcQuestions[i];
      if (
        !q.question?.trim() ||
        !q.correct?.trim() ||
        !q.correctExplain?.trim() ||
        !q.wrong1?.trim() ||
        !q.wrong2?.trim() ||
        !q.wrong3?.trim() ||
        !q.wrongExplain1?.trim() ||
        !q.wrongExplain2?.trim() ||
        !q.wrongExplain3?.trim()
      ) {
        setMessage({
          type: 'error',
          text: `Question ${i + 1} is missing required fields`,
        });
        return;
      }
    }

    setMcInsertLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/data/insert-multiple-choice', {
        passageId: mcModalPassageId,
        level: levelInt,
        questions: generatedMcQuestions,
      });

      setMessage({
        type: 'success',
        text:
          response.data.message ||
          `Successfully inserted ${response.data.inserted_count} multiple-choice question(s)!`,
      });
      setShowMcModal(false);
      setGeneratedMcQuestions([]);
      setMcModalPassageId(null);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text:
          error.response?.data?.error ||
          error.response?.data?.details ||
          'Failed to insert multiple-choice questions',
      });
    } finally {
      setMcInsertLoading(false);
    }
  };

  const handleConfirmMcEdit = async () => {
    if (!mcEditSelectedPassageId) {
      setMessage({ type: 'error', text: 'Please select a passage' });
      return;
    }

    setMcEditFetchLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.get('/data/multiple-choice', {
        params: { passageId: mcEditSelectedPassageId },
      });
      const rows = response.data.questions || [];
      setEditMcRows(rows);
      setMcEditLoadedPassageId(mcEditSelectedPassageId);
      if (rows.length === 0) {
        setMessage({
          type: 'success',
          text: 'No multiple-choice rows for this passage yet.',
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text:
          error.response?.data?.error ||
          error.response?.data?.details ||
          'Failed to load multiple-choice questions',
      });
      setEditMcRows([]);
      setMcEditLoadedPassageId(null);
    } finally {
      setMcEditFetchLoading(false);
    }
  };

  const handleUpdateEditMcQuestion = (index, field, value) => {
    const updated = [...editMcRows];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setEditMcRows(updated);
  };

  const handleSaveMcEdit = async (e) => {
    e.preventDefault();

    if (!mcEditLoadedPassageId || editMcRows.length === 0) {
      setMessage({ type: 'error', text: 'Nothing to save' });
      return;
    }

    for (let i = 0; i < editMcRows.length; i++) {
      const q = editMcRows[i];
      if (
        !q.question?.trim() ||
        !q.correct?.trim() ||
        !q.correctExplain?.trim() ||
        !q.wrong1?.trim() ||
        !q.wrong2?.trim() ||
        !q.wrong3?.trim() ||
        !q.wrongExplain1?.trim() ||
        !q.wrongExplain2?.trim() ||
        !q.wrongExplain3?.trim() ||
        q.multipleChoiceId === undefined ||
        q.multipleChoiceId === null
      ) {
        setMessage({
          type: 'error',
          text: `Question ${i + 1} is missing required fields`,
        });
        return;
      }
      const levelInt = parseInt(q.level, 10);
      if (Number.isNaN(levelInt)) {
        setMessage({
          type: 'error',
          text: `Question ${i + 1}: level must be a valid integer`,
        });
        return;
      }
    }

    setMcEditSaveLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/data/multiple-choice', {
        passageId: mcEditLoadedPassageId,
        questions: editMcRows,
      });
      setMessage({
        type: 'success',
        text: 'Multiple-choice questions saved successfully.',
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text:
          error.response?.data?.error ||
          error.response?.data?.details ||
          'Failed to save multiple-choice questions',
      });
    } finally {
      setMcEditSaveLoading(false);
    }
  };

  const resetMcEditPassage = () => {
    setMcEditLoadedPassageId(null);
    setEditMcRows([]);
    setMcEditSearchQuery('');
    setMcEditSearchColumns({ ...defaultMcEditSearchColumns });
  };

  const toggleMcEditSearchColumn = (key) => {
    setMcEditSearchColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const lqFilteredItems = useMemo(() => {
    const q = lqSearchQuery.trim().toLowerCase();
    const activeKeys = LQ_SEARCH_COLUMNS.filter(({ key }) => lqSearchColumns[key]).map(({ key }) => key);
    if (!q) {
      return lqRows.map((row, originalIndex) => ({ row, originalIndex }));
    }
    if (activeKeys.length === 0) {
      return [];
    }
    return lqRows
      .map((row, originalIndex) => ({ row, originalIndex }))
      .filter(({ row }) =>
        activeKeys.some((col) => {
          if (col === 'answerValue') {
            return (row.answers ?? []).some((ans) =>
              String(ans.answerValue ?? '').toLowerCase().includes(q)
            );
          }
          return String(row[col] ?? '').toLowerCase().includes(q);
        })
      );
  }, [lqRows, lqSearchQuery, lqSearchColumns]);

  const lqSearchNeedsColumns = lqSearchQuery.trim() !== '' && Object.values(lqSearchColumns).every((v) => !v);
  const lqSearchFilteringActive = lqSearchQuery.trim() !== '' && Object.values(lqSearchColumns).some((v) => v);

  const handleConfirmLqEdit = async () => {
    if (!lqSelectedPassageId) return;
    setLqFetchLoading(true);
    try {
      const res = await api.get(`/data/long-question?passageId=${lqSelectedPassageId}`);
      setLqRows(res.data.rows);
      setLqLoadedPassageId(lqSelectedPassageId);
      setLqSearchQuery('');
      setLqSearchColumns({ ...defaultLqSearchColumns });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to load long questions' });
    } finally {
      setLqFetchLoading(false);
    }
  };

  const handleUpdateLqQuestion = (index, value) => {
    setLqRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], questionValue: value };
      return updated;
    });
  };

  const handleUpdateLqAnswer = (rowIndex, answerIndex, value) => {
    setLqRows((prev) => {
      const updated = [...prev];
      const answers = [...updated[rowIndex].answers];
      answers[answerIndex] = { ...answers[answerIndex], answerValue: value };
      updated[rowIndex] = { ...updated[rowIndex], answers };
      return updated;
    });
  };

  const handleSaveLqEdit = async (e) => {
    e.preventDefault();
    if (!lqLoadedPassageId || lqRows.length === 0) return;
    setLqSaveLoading(true);
    try {
      await api.put('/data/long-question', { rows: lqRows });
      setMessage({ type: 'success', text: `Saved ${lqRows.length} row(s) successfully` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save long questions' });
    } finally {
      setLqSaveLoading(false);
    }
  };

  const resetLqPassage = () => {
    setLqLoadedPassageId(null);
    setLqRows([]);
    setLqSearchQuery('');
    setLqSearchColumns({ ...defaultLqSearchColumns });
  };

  const toggleLqSearchColumn = (key) => {
    setLqSearchColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="Database Management" />

      <div className="dashboard-layout">
        <AdminSidebar
          tables={tables}
          dashboardNav={{
            activeView,
            selectedTable,
            setActiveView,
            setSelectedTable,
          }}
        />

        <div className="dashboard-content">
          {message.text && (
            <div className={`message ${message.type}`}>{message.text}</div>
          )}

          {activeView === 'long-question' && (
            <div className="form-section long-qa-section">
              <h2 className="section-title">Long Question</h2>
              <p className="database-target">Database: {dbName} (port 5433)</p>
              
              {/* AI Generation Form */}
              <div className="ai-generation-section">
                <h3 className="subsection-title">AI-Generated Questions</h3>
                <form onSubmit={handleGenerateQuestions} className="ai-generation-form">
                  <div className="form-group">
                    <label>AI Prompt <span className="required">*</span></label>
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      required
                      rows={4}
                      placeholder="Enter your prompt for AI generation..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Number of Questions <span className="required">*</span></label>
                    <input
                      type="number"
                      value={questionCount}
                      onChange={(e) => setQuestionCount(parseInt(e.target.value) || 1)}
                      required
                      min="1"
                      max="40"
                      placeholder="Enter number of questions to generate"
                    />
                  </div>

                  <button type="submit" disabled={generationLoading} className="submit-button">
                    {generationLoading ? 'Generating...' : 'Generate Questions'}
                  </button>
                </form>
              </div>

              <div className="divider"></div>

              {/* Manual Entry Form */}
              <h3 className="subsection-title">Manual Entry</h3>
              <form onSubmit={handleLongQASubmit} className="long-qa-form">
                <div className="form-group">
                  <label>Question Text <span className="required">*</span></label>
                  <textarea
                    value={longQuestionValue}
                    onChange={(e) => setLongQuestionValue(e.target.value)}
                    required
                    rows={4}
                    placeholder="Enter the question text here..."
                  />
                </div>

                <div className="form-group">
                  <label>Select Passage <span className="required">*</span></label>
                  <div className="passage-list">
                    {passagesLoading ? (
                      <div className="loading-state">Loading passages...</div>
                    ) : passagesError ? (
                      <div className="error-state">
                        <p>{passagesError}</p>
                        <button 
                          type="button" 
                          onClick={fetchPassages}
                          className="retry-button"
                        >
                          Retry
                        </button>
                      </div>
                    ) : passages.length === 0 ? (
                      <div className="empty-state">
                        <p>No passages available</p>
                      </div>
                    ) : (
                      passages.map((passage) => (
                        <div
                          key={passage.passage_id}
                          className={`passage-item ${selectedPassageId === passage.passage_id ? 'active' : ''}`}
                          onClick={() => setSelectedPassageId(passage.passage_id)}
                        >
                          {passage.title}
                        </div>
                      ))
                    )}
                  </div>
                  {selectedPassageId && (
                    <div className="selected-passage">
                      Selected: {passages.find(p => p.passage_id === selectedPassageId)?.title}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Answers (one per line) <span className="required">*</span></label>
                  <textarea
                    value={longAnswerLines}
                    onChange={(e) => setLongAnswerLines(e.target.value)}
                    required
                    rows={6}
                    placeholder="Enter answers, one per line. Each line will become a separate answer row."
                  />
                  <small className="form-hint">Each line will be inserted as a separate answer with auto-incrementing order.</small>
                </div>

                <button type="submit" disabled={longQALoading} className="submit-button">
                  {longQALoading ? 'Inserting...' : 'Insert Question & Answers'}
                </button>
              </form>

              <div className="divider"></div>

              <div className="ai-generation-section">
                <h3 className="subsection-title">Edit</h3>
                <form onSubmit={handleSaveLqEdit} className="ai-generation-form">
                  <div className="mc-edit-filters">
                    <h4 className="mc-edit-filters-heading">Filters</h4>
                    <p className="form-hint mc-edit-filters-intro">
                      Choose a passage, confirm to load rows, then optionally narrow with search.
                    </p>

                    <div className="form-group">
                      <label>
                        Passage <span className="required">*</span>
                      </label>
                      <small className="form-hint mc-edit-passage-hint">
                        Which passage&apos;s long questions to load and edit.
                      </small>
                      <div className="passage-list">
                        {passagesLoading ? (
                          <div className="loading-state">Loading passages...</div>
                        ) : passagesError ? (
                          <div className="error-state">
                            <p>{passagesError}</p>
                            <button type="button" onClick={fetchPassages} className="retry-button">
                              Retry
                            </button>
                          </div>
                        ) : passages.length === 0 ? (
                          <div className="empty-state">
                            <p>No passages available</p>
                          </div>
                        ) : (
                          passages.map((passage) => (
                            <div
                              key={passage.passage_id}
                              className={`passage-item ${
                                lqSelectedPassageId === passage.passage_id ? 'active' : ''
                              }`}
                              onClick={() => {
                                setLqSelectedPassageId(passage.passage_id);
                                if (lqLoadedPassageId && lqLoadedPassageId !== passage.passage_id) {
                                  resetLqPassage();
                                }
                              }}
                            >
                              {passage.title}
                            </div>
                          ))
                        )}
                      </div>
                      {lqSelectedPassageId && (
                        <div className="selected-passage">
                          Selected:{' '}
                          {passages.find((p) => p.passage_id === lqSelectedPassageId)?.title}
                        </div>
                      )}
                    </div>

                    <div className="form-group mc-edit-search-section">
                      <h4 className="mc-edit-search-heading">Search (optional)</h4>
                      <label htmlFor="lq-edit-search-text">Search text</label>
                      <textarea
                        id="lq-edit-search-text"
                        value={lqSearchQuery}
                        onChange={(e) => setLqSearchQuery(e.target.value)}
                        rows={2}
                        placeholder="Filter loaded rows by substring (case-insensitive)…"
                        className="mc-edit-search-textarea"
                      />
                      <div
                        className="mc-edit-search-columns"
                        role="group"
                        aria-label="Columns to search"
                      >
                        {LQ_SEARCH_COLUMNS.map(({ key, label }) => (
                          <label key={key} className="mc-edit-search-checkbox-label">
                            <input
                              type="checkbox"
                              checked={!!lqSearchColumns[key]}
                              onChange={() => toggleLqSearchColumn(key)}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-group mc-edit-actions">
                    <button
                      type="button"
                      disabled={!lqSelectedPassageId || lqFetchLoading}
                      onClick={handleConfirmLqEdit}
                      className="submit-button"
                    >
                      {lqFetchLoading ? 'Loading...' : 'Confirm'}
                    </button>
                    {lqLoadedPassageId && (
                      <button type="button" className="cancel-button" onClick={resetLqPassage}>
                        Change passage
                      </button>
                    )}
                  </div>

                  {lqLoadedPassageId && (
                    <>
                      <div className="selected-passage mc-edit-editing-label">
                        Editing:{' '}
                        {passages.find((p) => p.passage_id === lqLoadedPassageId)?.title}
                      </div>
                      {!!lqSearchQuery.trim() && lqRows.length > 0 && (
                        <p className="form-hint mc-edit-filter-hint">
                          Showing {lqFilteredItems.length} of {lqRows.length} row
                          {lqRows.length === 1 ? '' : 's'}
                        </p>
                      )}
                      <div className="generated-questions-list">
                        {lqFilteredItems.map(({ row, originalIndex }) => (
                          <div key={row.longQuestionId ?? originalIndex} className="mc-question-group">
                            <h4 className="mc-question-group-title">#{originalIndex + 1}</h4>
                            <div className="form-group">
                              <label>Question (long_question.value)</label>
                              <textarea
                                value={row.questionValue ?? ''}
                                onChange={(e) =>
                                  handleUpdateLqQuestion(originalIndex, e.target.value)
                                }
                                rows={4}
                              />
                            </div>
                            {(row.answers ?? []).map((ans, ansIdx) => (
                              <div key={ans.longAnswerId ?? ansIdx} className="form-group">
                                <label>Answer {ansIdx + 1} (long_answer.value, order={ans.order})</label>
                                <textarea
                                  value={ans.answerValue ?? ''}
                                  onChange={(e) =>
                                    handleUpdateLqAnswer(originalIndex, ansIdx, e.target.value)
                                  }
                                  rows={4}
                                />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                      {lqRows.length === 0 && !lqFetchLoading && (
                        <p className="form-hint">No long question rows for this passage.</p>
                      )}
                      {lqRows.length > 0 && lqFilteredItems.length === 0 && lqSearchNeedsColumns && (
                        <p className="form-hint">
                          Select at least one column to filter by search text, or clear the search
                          box to show all rows for this passage.
                        </p>
                      )}
                      {lqRows.length > 0 &&
                        lqFilteredItems.length === 0 &&
                        lqSearchFilteringActive && (
                          <p className="form-hint">
                            No rows match the current search. Clear the search text or adjust column
                            checkboxes.
                          </p>
                        )}
                      <button
                        type="submit"
                        disabled={lqSaveLoading || lqRows.length === 0 || !lqLoadedPassageId}
                        className="submit-button"
                      >
                        {lqSaveLoading ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                </form>
              </div>
            </div>
          )}

          {/* Generation Modal */}
          {showGenerationModal && (
            <div className="modal-overlay" onClick={() => setShowGenerationModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Review & Insert Generated Questions</h2>
                  <button 
                    className="modal-close-button"
                    onClick={() => setShowGenerationModal(false)}
                  >
                    ×
                  </button>
                </div>
                
                <form onSubmit={handleInsertGeneratedQuestions} className="modal-form">
                  <div className="form-group">
                    <label>Select Passage <span className="required">*</span></label>
                    <div className="passage-list">
                      {passagesLoading ? (
                        <div className="loading-state">Loading passages...</div>
                      ) : passagesError ? (
                        <div className="error-state">
                          <p>{passagesError}</p>
                          <button 
                            type="button" 
                            onClick={fetchPassages}
                            className="retry-button"
                          >
                            Retry
                          </button>
                        </div>
                      ) : passages.length === 0 ? (
                        <div className="empty-state">
                          <p>No passages available</p>
                        </div>
                      ) : (
                        passages.map((passage) => (
                          <div
                            key={passage.passage_id}
                            className={`passage-item ${modalPassageId === passage.passage_id ? 'active' : ''}`}
                            onClick={() => setModalPassageId(passage.passage_id)}
                          >
                            {passage.title}
                          </div>
                        ))
                      )}
                    </div>
                    {modalPassageId && (
                      <div className="selected-passage">
                        Selected: {passages.find(p => p.passage_id === modalPassageId)?.title}
                      </div>
                    )}
                  </div>

                  <div className="generated-questions-list">
                    {generatedQuestions.map((question, index) => (
                      <div key={index} className="generated-question-item">
                        <div className="question-item-header">
                          <h4 className="question-item-title">Question {index + 1}</h4>
                          <button
                            type="button"
                            className="delete-question-button"
                            onClick={() => handleDeleteGeneratedQuestion(index)}
                            title="Delete this question"
                          >
                            Delete
                          </button>
                        </div>
                        <div className="form-group">
                          <label>Question Text <span className="required">*</span></label>
                          <textarea
                            value={question.questionText}
                            onChange={(e) => handleUpdateGeneratedQuestion(index, 'questionText', e.target.value)}
                            required
                            rows={4}
                            placeholder="Question text..."
                          />
                        </div>
                        <div className="form-group">
                          <label>Answer <span className="required">*</span></label>
                          <textarea
                            value={question.answerText}
                            onChange={(e) => handleUpdateGeneratedQuestion(index, 'answerText', e.target.value)}
                            required
                            rows={6}
                            placeholder="Answer text..."
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="cancel-button"
                      onClick={() => setShowGenerationModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={insertLoading} 
                      className="submit-button"
                    >
                      {insertLoading ? 'Inserting...' : 'Insert Questions & Answers'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeView === 'keywords' && (
            <div className="form-section long-qa-section">
              <h2 className="section-title">Keyword</h2>
              <p className="database-target">Database: {dbName} (port 5433)</p>
              
              {/* AI Generation Form */}
              <div className="ai-generation-section">
                <h3 className="subsection-title">AI-Generated Keywords</h3>
                <form onSubmit={handleGenerateKeywords} className="ai-generation-form">
                  <div className="form-group">
                    <label>AI Prompt <span className="required">*</span></label>
                    <textarea
                      value={keywordPrompt}
                      onChange={(e) => setKeywordPrompt(e.target.value)}
                      required
                      rows={4}
                      placeholder="Enter your prompt for AI generation..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Number of Keywords <span className="required">*</span></label>
                    <input
                      type="number"
                      value={keywordCount}
                      onChange={(e) => setKeywordCount(parseInt(e.target.value) || 1)}
                      required
                      min="1"
                      max="100"
                      placeholder="Enter number of keywords to generate"
                    />
                  </div>

                  <button type="submit" disabled={keywordGenerationLoading} className="submit-button">
                    {keywordGenerationLoading ? 'Generating...' : 'Generate Keywords'}
                  </button>
                </form>
              </div>

              <div className="divider"></div>

              {/* Manual Entry Form */}
              <h3 className="subsection-title">Manual Entry</h3>
              <form onSubmit={handleKeywordSubmit} className="long-qa-form">
                <div className="form-group">
                  <label>Select Passage <span className="required">*</span></label>
                  <div className="passage-list">
                    {passagesLoading ? (
                      <div className="loading-state">Loading passages...</div>
                    ) : passagesError ? (
                      <div className="error-state">
                        <p>{passagesError}</p>
                        <button 
                          type="button" 
                          onClick={fetchPassages}
                          className="retry-button"
                        >
                          Retry
                        </button>
                      </div>
                    ) : passages.length === 0 ? (
                      <div className="empty-state">
                        <p>No passages available</p>
                      </div>
                    ) : (
                      passages.map((passage) => (
                        <div
                          key={passage.passage_id}
                          className={`passage-item ${selectedKeywordPassageId === passage.passage_id ? 'active' : ''}`}
                          onClick={() => setSelectedKeywordPassageId(passage.passage_id)}
                        >
                          {passage.title}
                        </div>
                      ))
                    )}
                  </div>
                  {selectedKeywordPassageId && (
                    <div className="selected-passage">
                      Selected: {passages.find(p => p.passage_id === selectedKeywordPassageId)?.title}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Keywords (one per line) <span className="required">*</span></label>
                  <textarea
                    value={keywordLines}
                    onChange={(e) => setKeywordLines(e.target.value)}
                    required
                    rows={6}
                    placeholder="Enter keywords, one per line. Each line will become a separate keyword row."
                  />
                  <small className="form-hint">Each line will be inserted as a separate keyword with auto-incrementing ID.</small>
                </div>

                <button type="submit" disabled={keywordLoading} className="submit-button">
                  {keywordLoading ? 'Inserting...' : 'Insert Keywords'}
                </button>
              </form>
            </div>
          )}

          {activeView === 'multiple-choice' && (
            <div className="form-section long-qa-section">
              <h2 className="section-title">Multiple Choice</h2>
              <p className="database-target">Database: {dbName} (port 5433)</p>

              <div className="ai-generation-section">
                <h3 className="subsection-title">AI-Generated Questions</h3>
                <form onSubmit={handleGenerateMcQuestions} className="ai-generation-form">
                  <div className="form-group">
                    <label>
                      AI Prompt <span className="required">*</span>
                    </label>
                    <textarea
                      value={mcAiPrompt}
                      onChange={(e) => setMcAiPrompt(e.target.value)}
                      required
                      rows={4}
                      placeholder="Enter your prompt for AI generation..."
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Number of Questions <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      value={mcQuestionCount}
                      onChange={(e) => setMcQuestionCount(parseInt(e.target.value, 10) || 1)}
                      required
                      min="1"
                      max="40"
                      placeholder="Enter number of questions to generate"
                    />
                  </div>

                  <button type="submit" disabled={mcGenerationLoading} className="submit-button">
                    {mcGenerationLoading ? 'Generating...' : 'Generate Questions'}
                  </button>
                </form>
              </div>

              <div className="ai-generation-section">
                <h3 className="subsection-title">Edit</h3>
                <form onSubmit={handleSaveMcEdit} className="ai-generation-form">
                  <div className="mc-edit-filters">
                    <h4 className="mc-edit-filters-heading">Filters</h4>
                    <p className="form-hint mc-edit-filters-intro">
                      Search by title, pick a passage, confirm to load rows, then optionally
                      narrow with search.
                    </p>

                    <div className="form-group">
                      <small className="form-hint mc-edit-passage-hint">
                        Which passage&apos;s questions to load and edit.
                      </small>
                      <PassagePicker
                        inputId="mc-edit-passage-picker"
                        label={
                          <>
                            Passage <span className="required">*</span>
                          </>
                        }
                        passages={mcPassages}
                        passagesLoading={mcPassagesLoading}
                        passagesError={mcPassagesError}
                        value={mcEditSelectedPassageId ?? ''}
                        onChange={(nextId) => {
                          const id = nextId === '' ? null : parseInt(nextId, 10);
                          setMcEditSelectedPassageId(id);
                          if (
                            mcEditLoadedPassageId != null &&
                            mcEditLoadedPassageId !== id
                          ) {
                            resetMcEditPassage();
                          }
                        }}
                        extraDisabled={mcEditFetchLoading}
                      />
                      {mcEditSelectedPassageId && (
                        <div className="selected-passage">
                          Selected:{' '}
                          {
                            mcPassages.find((p) => p.passage_id === mcEditSelectedPassageId)
                              ?.title
                          }
                        </div>
                      )}
                    </div>

                    <div className="form-group mc-edit-search-section">
                      <h4 className="mc-edit-search-heading">Search (optional)</h4>
                      <label htmlFor="mc-edit-search-text">Search text</label>
                      <textarea
                        id="mc-edit-search-text"
                        value={mcEditSearchQuery}
                        onChange={(e) => setMcEditSearchQuery(e.target.value)}
                        rows={2}
                        placeholder="Filter loaded rows by substring (case-insensitive)…"
                        className="mc-edit-search-textarea"
                      />
                      <div
                        className="mc-edit-search-columns"
                        role="group"
                        aria-label="Columns to search"
                      >
                        {MC_EDIT_SEARCH_COLUMNS.map(({ key, label }) => (
                          <label key={key} className="mc-edit-search-checkbox-label">
                            <input
                              type="checkbox"
                              checked={!!mcEditSearchColumns[key]}
                              onChange={() => toggleMcEditSearchColumn(key)}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="form-group mc-edit-actions">
                    <button
                      type="button"
                      disabled={!mcEditSelectedPassageId || mcEditFetchLoading}
                      onClick={handleConfirmMcEdit}
                      className="submit-button"
                    >
                      {mcEditFetchLoading ? 'Loading...' : 'Confirm'}
                    </button>
                    {mcEditLoadedPassageId && (
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={resetMcEditPassage}
                      >
                        Change passage
                      </button>
                    )}
                  </div>

                  {mcEditLoadedPassageId && (
                    <>
                      <div className="selected-passage mc-edit-editing-label">
                        Editing:{' '}
                        {
                          mcPassages.find((p) => p.passage_id === mcEditLoadedPassageId)
                            ?.title
                        }
                      </div>
                      {!!mcEditSearchQuery.trim() && editMcRows.length > 0 && (
                        <p className="form-hint mc-edit-filter-hint">
                          Showing {mcEditFilteredItems.length} of {editMcRows.length}{' '}
                          row{editMcRows.length === 1 ? '' : 's'}
                        </p>
                      )}
                      <div className="generated-questions-list">
                        {mcEditFilteredItems.map(({ row: question, originalIndex: index }) => (
                          <McQuestionFieldGroup
                            key={question.multipleChoiceId ?? index}
                            question={question}
                            index={index}
                            onFieldChange={handleUpdateEditMcQuestion}
                            showDelete={false}
                            perRowLevel
                          />
                        ))}
                      </div>
                      {editMcRows.length === 0 && !mcEditFetchLoading && (
                        <p className="form-hint">
                          No multiple-choice rows for this passage.
                        </p>
                      )}
                      {editMcRows.length > 0 &&
                        mcEditFilteredItems.length === 0 &&
                        mcEditSearchNeedsColumns && (
                          <p className="form-hint">
                            Select at least one column to filter by search text, or clear the
                            search box to show all rows for this passage.
                          </p>
                        )}
                      {editMcRows.length > 0 &&
                        mcEditFilteredItems.length === 0 &&
                        mcEditSearchFilteringActive && (
                          <p className="form-hint">
                            No rows match the current search. Clear the search text or adjust
                            column checkboxes.
                          </p>
                        )}
                      <button
                        type="submit"
                        disabled={
                          mcEditSaveLoading ||
                          editMcRows.length === 0 ||
                          !mcEditLoadedPassageId
                        }
                        className="submit-button"
                      >
                        {mcEditSaveLoading ? 'Saving...' : 'Save'}
                      </button>
                    </>
                  )}
                </form>
              </div>
            </div>
          )}

          {/* Keyword Generation Modal */}
          {showKeywordModal && (
            <div className="modal-overlay" onClick={() => setShowKeywordModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Review & Insert Generated Keywords</h2>
                  <button 
                    className="modal-close-button"
                    onClick={() => setShowKeywordModal(false)}
                  >
                    ×
                  </button>
                </div>
                
                <form onSubmit={handleInsertGeneratedKeywords} className="modal-form">
                  <div className="form-group">
                    <label>Select Passage <span className="required">*</span></label>
                    <div className="passage-list">
                      {passagesLoading ? (
                        <div className="loading-state">Loading passages...</div>
                      ) : passagesError ? (
                        <div className="error-state">
                          <p>{passagesError}</p>
                          <button 
                            type="button" 
                            onClick={fetchPassages}
                            className="retry-button"
                          >
                            Retry
                          </button>
                        </div>
                      ) : passages.length === 0 ? (
                        <div className="empty-state">
                          <p>No passages available</p>
                        </div>
                      ) : (
                        passages.map((passage) => (
                          <div
                            key={passage.passage_id}
                            className={`passage-item ${keywordModalPassageId === passage.passage_id ? 'active' : ''}`}
                            onClick={() => setKeywordModalPassageId(passage.passage_id)}
                          >
                            {passage.title}
                          </div>
                        ))
                      )}
                    </div>
                    {keywordModalPassageId && (
                      <div className="selected-passage">
                        Selected: {passages.find(p => p.passage_id === keywordModalPassageId)?.title}
                      </div>
                    )}
                  </div>

                  <div className="generated-questions-list">
                    {generatedKeywords.map((keyword, index) => (
                      <div key={index} className="generated-question-item">
                        <div className="question-item-header">
                          <h4 className="question-item-title">Keyword {index + 1}</h4>
                          <button
                            type="button"
                            className="delete-question-button"
                            onClick={() => handleDeleteGeneratedKeyword(index)}
                            title="Delete this keyword"
                          >
                            Delete
                          </button>
                        </div>
                        <div className="form-group">
                          <label>Keyword <span className="required">*</span></label>
                          <input
                            type="text"
                            value={keyword}
                            onChange={(e) => handleUpdateGeneratedKeyword(index, e.target.value)}
                            required
                            placeholder="Keyword text..."
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="cancel-button"
                      onClick={() => setShowKeywordModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      disabled={keywordInsertLoading} 
                      className="submit-button"
                    >
                      {keywordInsertLoading ? 'Inserting...' : 'Insert Keywords'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showMcModal && (
            <div className="modal-overlay" onClick={() => setShowMcModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Review &amp; insert multiple choice</h2>
                  <button
                    type="button"
                    className="modal-close-button"
                    onClick={() => setShowMcModal(false)}
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleInsertMcQuestions} className="modal-form">
                  <div className="form-group">
                    <PassagePicker
                      inputId="mc-modal-passage-picker"
                      label={
                        <>
                          Select Passage <span className="required">*</span>
                        </>
                      }
                      passages={mcPassages}
                      passagesLoading={mcPassagesLoading}
                      passagesError={mcPassagesError}
                      value={mcModalPassageId ?? ''}
                      onChange={(nextId) => {
                        const id = nextId === '' ? null : parseInt(nextId, 10);
                        setMcModalPassageId(id);
                      }}
                      extraDisabled={mcInsertLoading}
                    />
                    {mcModalPassageId && (
                      <div className="selected-passage">
                        Selected:{' '}
                        {mcPassages.find((p) => p.passage_id === mcModalPassageId)?.title}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>
                      Level <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      value={mcLevel}
                      onChange={(e) => setMcLevel(parseInt(e.target.value, 10) || 0)}
                      required
                      min="0"
                      placeholder="Difficulty level (integer)"
                    />
                  </div>

                  <div className="generated-questions-list">
                    {generatedMcQuestions.map((question, index) => (
                      <McQuestionFieldGroup
                        key={index}
                        question={question}
                        index={index}
                        onFieldChange={handleUpdateMcQuestion}
                        showDelete
                        onDelete={handleDeleteMcQuestion}
                        perRowLevel={false}
                      />
                    ))}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => setShowMcModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={mcInsertLoading}
                      className="submit-button"
                    >
                      {mcInsertLoading ? 'Inserting...' : 'Insert multiple choice'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeView === 'tables' && (
            <div className="form-section">
              {selectedTable && columns.length > 0 ? (
              <form onSubmit={handleSubmit} className="insert-form">
                <h2>Insert Data into {selectedTable}</h2>
                {columns.map((column) => {
                  if (column.column_default) {
                    return null;
                  }
                  return (
                    <div key={column.column_name} className="form-group">
                      <label>
                        {column.column_name}
                        <span className="column-type">({column.data_type})</span>
                        {column.is_nullable === 'NO' && (
                          <span className="required">*</span>
                        )}
                      </label>
                      {column.data_type === 'boolean' ? (
                        <select
                          value={formData[column.column_name] || ''}
                          onChange={(e) =>
                            handleInputChange(column.column_name, e.target.value)
                          }
                          required={column.is_nullable === 'NO'}
                        >
                          <option value="">-- Select --</option>
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      ) : column.data_type.includes('text') || column.data_type === 'character varying' ? (
                        <textarea
                          value={formData[column.column_name] || ''}
                          onChange={(e) =>
                            handleInputChange(column.column_name, e.target.value)
                          }
                          required={column.is_nullable === 'NO'}
                          rows={3}
                        />
                      ) : (
                        <input
                          type={
                            column.data_type === 'integer' ||
                            column.data_type === 'bigint' ||
                            column.data_type === 'numeric'
                              ? 'number'
                              : column.data_type === 'date'
                              ? 'date'
                              : column.data_type === 'timestamp'
                              ? 'datetime-local'
                              : 'text'
                          }
                          value={formData[column.column_name] || ''}
                          onChange={(e) =>
                            handleInputChange(column.column_name, e.target.value)
                          }
                          required={column.is_nullable === 'NO'}
                        />
                      )}
                    </div>
                  );
                })}
                <button type="submit" disabled={loading} className="submit-button">
                  {loading ? 'Inserting...' : 'Insert Data'}
                </button>
              </form>
            ) : selectedTable ? (
              <div className="loading-state">Loading table structure...</div>
            ) : (
              <div className="empty-state">
                <p>Select a table from the sidebar to insert data</p>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
